'use client';

import { FC } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { TabBar } from '../../../common/components/TabBar/TabBar';
import { useTransfersState } from '../../utils/useTransfersState';
import { TransfersSectionVariants } from '../../types';
import { DepositScreen } from '../DepositScreen/DepositScreen';
import { SendScreen } from '../SendScreen/SendScreen';
import { TCryptoTransferTranslations } from '../../utils/getCryptoTransferTranslations';
import { useTransfersNextStepState } from '../../utils/useTransfersNextStepState.client';
import { useTransfersAssetsListContext } from '../TransfersAssetListContext/TransfersAssetListContext';
import {
  AccessInfoType,
  TriggerSetupTwoFaModal,
  UserAccessModal,
} from '../../../common';
import { useClientUserInfo } from '../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';

export interface ITransfersParentProps {
  translations: TCryptoTransferTranslations;
  transferType?: TransfersSectionVariants;
}

const TransfersParent: FC<ITransfersParentProps> = ({
  translations,
  transferType,
}) => {
  const { isReadOnlyUser, twoFactorAuthentication } = useClientUserInfo();

  const { closeAll, showModal } = useModalDrawerContext();

  const { currentSection, setCurrentSection } = useTransfersState(transferType);

  const { assetsListForDeposit, assetsListForSend } =
    useTransfersAssetsListContext();

  const { nextStepState: depositState } = useTransfersNextStepState({
    closeTransferModule: () => closeAll(),
    assetList: assetsListForDeposit,
    transferType: TransfersSectionVariants.Deposit,
  });

  const { nextStepState: sendState } = useTransfersNextStepState({
    closeTransferModule: () => closeAll(),
    assetList: assetsListForSend,
    transferType: TransfersSectionVariants.Send,
  });

  const bothStatesAreClean =
    depositState.isStateClean && sendState.isStateClean;

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: {
          md: '460px',
        },
        mx: {
          md: 'auto',
        },
        height: {
          base: '100vh',
        },
      })}
    >
      {!transferType && bothStatesAreClean && (
        <TabBar
          onValueChange={(newValue) => {
            if (newValue === TransfersSectionVariants.Send) {
              if (twoFactorAuthentication !== 'ENABLED') {
                showModal(TriggerSetupTwoFaModal, {
                  onModalClose: () => {
                    setCurrentSection(TransfersSectionVariants.Deposit);
                  },
                  disableOverlayClose: true,
                });

                return;
              }

              if (isReadOnlyUser) {
                showModal(UserAccessModal, {
                  type: AccessInfoType.ReadOnly,
                  onModalClose: () => {
                    setCurrentSection(TransfersSectionVariants.Deposit);
                  },
                });

                return;
              }
            }

            setCurrentSection(newValue as TransfersSectionVariants);
          }}
          value={currentSection}
          tabItems={[
            {
              label: translations.deposit,
              value: TransfersSectionVariants.Deposit,
              disabled: false,
            },
            {
              label: translations.send,
              value: TransfersSectionVariants.Send,
              disabled: assetsListForSend && assetsListForSend.length === 0,
            },
          ]}
        />
      )}
      {currentSection === TransfersSectionVariants.Deposit ? (
        <DepositScreen
          state={depositState}
          transfersTranslations={translations}
        />
      ) : (
        <SendScreen state={sendState} transfersTranslations={translations} />
      )}
    </div>
  );
};

export { TransfersParent };
